import React, { useEffect, useRef } from 'react'
import { FREE_SERVICES } from '../../../../../constants/policies'
import { CheckCircleOutlined } from '@ant-design/icons'

import './styles.scss'
import { Button, Checkbox, DatePicker, Divider, Form, Input, Radio } from 'antd'
import { TAX_PERCENTAGE } from '../../../../../constants/finance'
import usePageSettings from '../../../../../hooks/usePageSettings'
import moment from 'moment'

const FixedTourConfirmation = ({
    userTourData,
    setUserTourData,
    tourPackage,
    messageApi,
    prev,
    next,
    calculateTotal,
    calculateSubTotal,
    calculateDiscount,
    calculateTax,
    current,
}) => {
    const formRef = useRef(null)
    const selectedCurrency = usePageSettings()?.selectedCurrency;

    useEffect(() => {
        window.scrollTo(0, 0)
        formRef?.current?.setFieldsValue({
            flightBooked: userTourData?.filghtDetails?.flightBooked,
            flightNumber: userTourData.filghtDetails?.flightNumber,
            departureAirport: userTourData.filghtDetails?.departureAirport,
            arrivalDateTime: userTourData.filghtDetails?.arrivalDateTime,
            confirmation: false,
        })
    }, [])

    const handleGoNext = (values) => {
        if (!userTourData.startDate) {
            messageApi.open({
                type: 'error',
                message: 'Start Date is required',
                description: 'Please go back and select a start date for the tour.',
            })
            return
        }

        if (!userTourData.userData || !userTourData.userData.totalTravelers) {
            messageApi.open({
                type: 'error',
                message: 'Traveler Information is required',
                description: 'Please go back and provide traveler information.',
            })
            return
        }

        if (!values.confirmation) {
            messageApi.open({
                type: 'error',
                message: 'Confirmation is required',
                description: 'Please confirm that the details are correct and agree to the terms and conditions.',
            })
            return
        }

        const flightDetails = {
            flightBooked: values.flightBooked || false,
            flightNumber: values.flightNumber || '',
            departureAirport: values.departureAirport || '',
            arrivalDateTime: values.arrivalDateTime ? moment(values.arrivalDateTime).format('YYYY-MM-DD HH:mm') : ''
        }

        setUserTourData({
            ...userTourData,
            filghtDetails: flightDetails,
            termsAndConditions: values.confirmation
        })

        next()
    }

    const getTravelersList = () => {
        const travelers = [];
        if (userTourData.userData && userTourData.userData.totalTravelers) {
            for (let i = 1; i <= userTourData.userData.totalTravelers; i++) {
                travelers.push({
                    firstName: userTourData.userData[`traveler${i}FirstName`],
                    lastName: userTourData.userData[`traveler${i}LastName`],
                    email: userTourData.userData[`traveler${i}Email`],
                    phone: userTourData.userData[`traveler${i}Phone`],
                    country: userTourData.userData[`traveler${i}Country`],
                    passport: userTourData.userData[`traveler${i}PassportNumber`]
                });
            }
        }
        return travelers;
    }

    return (
        <div className='tourBookingConfirmation'>
            <div className='left'>
                <div className='tour_package_details'>
                    <div className='tour_package_details_header'>
                        <h2>{tourPackage?.packageName}</h2>
                        <p>{tourPackage?.durationDays} Days / {tourPackage?.durationNights} Nights</p>
                        <p>Start Date: {userTourData?.startDate}</p>
                        <p>Total Travelers: {userTourData?.userData?.totalTravelers || 0}</p>
                    </div>
                </div>

                <div className='traveler_summary'>
                    <h3>Travelers</h3>
                    {getTravelersList().map((traveler, index) => (
                        <div key={index} className='traveler_item'>
                            <h4>Traveler {index + 1} {index === 0 && '(Primary Contact)'}</h4>
                            <p><strong>Name:</strong> {traveler.firstName} {traveler.lastName}</p>
                            <p><strong>Email:</strong> {traveler.email}</p>
                            <p><strong>Phone:</strong> {traveler.phone}</p>
                            <p><strong>Country:</strong> {traveler.country}</p>
                            <p><strong>Passport:</strong> {traveler.passport}</p>
                        </div>
                    ))}
                </div>

                <div className='pricing_details'>
                    <h3>Pricing Details</h3>
                    <div className='pricing_details_content'>
                        <div className='pricing_row'>
                            <span>Package Price (for {tourPackage?.maxGroupSize} {tourPackage?.maxGroupSize > 1 ? 'people' : 'person'})</span>
                            <span>{calculateSubTotal()} {selectedCurrency || 'AUD'}</span>
                        </div>
                        {tourPackage?.discount > 0 && (
                            <div className='pricing_row discount'>
                                <span>Discount ({tourPackage?.discount}%)</span>
                                <span>- {calculateDiscount()} {selectedCurrency || 'AUD'}</span>
                            </div>
                        )}
                        <div className='pricing_row'>
                            <span>Tax ({TAX_PERCENTAGE}%)</span>
                            <span>{calculateTax()} {selectedCurrency || 'AUD'}</span>
                        </div>
                        <Divider />
                        <div className='pricing_row total'>
                            <span>Total</span>
                            <span>{calculateTotal()} {selectedCurrency || 'AUD'}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className='right'>
                <div className='form_container'>
                    <h2>Flight Details</h2>
                    <p>Please let us know your flight details. If you don't have the details yet, you can provide them later. We'll confirm your tour booking once we have the details.</p>

                    <Form
                        layout='vertical'
                        onFinish={handleGoNext}
                        ref={formRef}
                        requiredMark={false}
                    >
                        <Form.Item
                            name='flightBooked'
                            label='Flight Status'
                        >
                            <Radio.Group>
                                <Radio value={true}>Yes, I have booked my flight</Radio>
                                <Radio value={false}>No, I haven't booked my flight yet</Radio>
                            </Radio.Group>
                        </Form.Item>

                        <Form.Item
                            noStyle
                            shouldUpdate={(prevValues, currentValues) => prevValues.flightBooked !== currentValues.flightBooked}
                        >
                            {({ getFieldValue }) => {
                                const flightBooked = getFieldValue('flightBooked');
                                return flightBooked ? (
                                    <>
                                        <Form.Item
                                            name='flightNumber'
                                            label='Flight Number'
                                            rules={[{ required: true, message: 'Please input flight number!' }]}
                                        >
                                            <Input placeholder="e.g., SQ123" />
                                        </Form.Item>

                                        <Form.Item
                                            name='departureAirport'
                                            label='Departure Airport'
                                            rules={[{ required: true, message: 'Please input departure airport!' }]}
                                        >
                                            <Input placeholder="e.g., Sydney (SYD)" />
                                        </Form.Item>

                                        <Form.Item
                                            name='arrivalDateTime'
                                            label='Arrival Date & Time in Sri Lanka'
                                            rules={[{ required: true, message: 'Please select arrival date and time!' }]}
                                        >
                                            <DatePicker
                                                showTime
                                                format="YYYY-MM-DD HH:mm"
                                                style={{ width: '100%' }}
                                                placeholder="Select arrival date and time"
                                            />
                                        </Form.Item>
                                    </>
                                ) : null;
                            }}
                        </Form.Item>

                        <Divider />

                        <h2>Confirmation</h2>
                        <p>Please make sure the traveler details are correct. If there are any mistakes, please go back and correct them. If everything is correct, please proceed to the payment.</p>

                        <Form.Item
                            name='confirmation'
                            valuePropName='checked'
                            rules={[{ required: true, message: 'Please confirm the details!' }]}
                        >
                            <Checkbox>
                                Above details are correct and I agree to the <a href='/terms-and-conditions' target='_blank'>Terms and Conditions</a> and <a href='/privacy-policy' target='_blank'>Privacy Policy</a>.
                            </Checkbox>
                        </Form.Item>

                        <div className='form_actions'>
                            <Button
                                onClick={prev}
                                className='prev_btn'
                                size='large'
                            >
                                Back
                            </Button>
                            <Button
                                type='primary'
                                htmlType='submit'
                                className='next_btn'
                                size='large'
                            >
                                Proceed to Payment
                            </Button>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    )
}

export default FixedTourConfirmation
